// src/pages/admin/useAdminData.js
import { useState, useCallback } from 'react';
import api from '../../utils/api';

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export function useAdminData() {

  const [users,       setUsers]       = useState([]);
  const [userStats,   setUserStats]   = useState({ total:0, customers:0, farmers:0, butchers:0, admins:0 });
  const [animals,     setAnimals]     = useState([]);
  const [sales,       setSales]       = useState([]);
  const [salesStats,  setSalesStats]  = useState({ total_revenue:0, total_orders:0 });
  const [inquiries,   setInquiries]   = useState([]);
  const [error,       setError]       = useState('');
  const [butcherProducts, setButcherProducts] = useState([]);
  const [breedStats,      setBreedStats]      = useState([]);
  const [recentActivity,  setRecentActivity]  = useState([]);
  const [auctions, setAuctions]        = useState([]);
  const [platformStats,   setPlatformStats]   = useState({
    total_orders: 0, total_animals: 0,
    total_users: 0, total_products: 0, pending_review: 0
  });

  // ── Split loading states ───────────────────────────────
  const [loadingUsers,  setLoadingUsers]  = useState(false);
  const [loadingData,   setLoadingData]   = useState(false);

  // ── Users ──────────────────────────────────────────────
  const fetchUsers = useCallback(async (q = '') => {
    setLoadingUsers(true); setError('');
    try {
      const { data } = await api.get(
        `/api/admin/users${q ? `?search=${encodeURIComponent(q)}` : ''}`
      );
      setUsers(data);
    } catch {
      setError('Could not load users from database.');
    } finally { setLoadingUsers(false); }
  }, []);

  // ── User stats ─────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/user-stats');
      setUserStats(data);
    } catch { /* silently fail */ }
  }, []);

  // ── Animals ────────────────────────────────────────────
  const fetchAnimals = useCallback(async (status = '') => {
    setLoadingData(true); setError('');
    try {
      const { data } = await api.get(
        `/api/admin/animals${status ? `?status=${status}` : ''}`
      );
      setAnimals(data);
    } catch {
      setError('Could not load listings from database.');
    } finally { setLoadingData(false); }
  }, []);

  // ── Moderation action ──────────────────────────────────
  const handleModAction = async (id, status) => {
    try {
      await api.patch(`/api/admin/animals/${id}/status`, { status }, {
        headers: authHeader()
      });
      setAnimals(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    } catch {
      alert('Action failed. Check backend.');
    }
  };

  // ── Sales ──────────────────────────────────────────────
  const fetchSales = useCallback(async () => {
    setLoadingData(true); setError('');
    try {
      const { data } = await api.get('/api/admin/sales');
      setSales(data);
      const totalRevenue = data.reduce((sum, s) => sum + Number(s.amount || 0), 0);
      setSalesStats({ total_revenue: totalRevenue, total_orders: data.length });
    } catch {
      setError('Could not load sales.');
    } finally { setLoadingData(false); }
  }, []);

  // ── Inquiries ──────────────────────────────────────────
  const fetchInquiries = useCallback(async () => {
    setLoadingData(true); setError('');
    try {
      const { data } = await api.get('/api/admin/inquiries');
      setInquiries(data);
    } catch {
      setError('Could not load inquiries.');
    } finally { setLoadingData(false); }
  }, []);

  // ── Butcher products ───────────────────────────────────
  const fetchButcherProducts = useCallback(async (status = '') => {
    try {
      const url = status
        ? `/api/admin/butcher-products?status=${status}`
        : '/api/admin/butcher-products';
      const { data } = await api.get(url, { headers: authHeader() });
      setButcherProducts(data);
    } catch (err) {
      console.error('fetchButcherProducts error:', err);
    }
  }, []);

  const handleProductModAction = async (id, status) => {
    try {
      await api.patch(`/api/admin/butcher-products/${id}/status`, { status }, {
        headers: authHeader()
      });
      fetchButcherProducts();
    } catch (err) {
      console.error('handleProductModAction error:', err);
    }
  };

  // ── Overview extras ────────────────────────────────────
  const fetchBreedStats = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/breed-stats', { headers: authHeader() });
      setBreedStats(data);
    } catch (err) {
      console.error('fetchBreedStats error:', err);
    }
  }, []);

  const fetchRecentActivity = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/recent-activity', { headers: authHeader() });
      setRecentActivity(data);
    } catch (err) {
      console.error('fetchRecentActivity error:', err);
    }
  }, []);

  const fetchPlatformStats = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/platform-stats', { headers: authHeader() });
      setPlatformStats(data);
    } catch (err) {
      console.error('fetchPlatformStats error:', err);
    }
  }, []);

  const fetchAuctions = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/auctions');
      setAuctions(data);
    } catch (err) {
      console.error('fetchAuctions error:', err);
    }
  }, []);

  const handleAuctionApproval = async (id, approval_status) => {
    try {
      await api.patch(`/api/admin/auctions/${id}/approve`, { approval_status });
      setAuctions(prev => prev.map(a => a.id === id ? { ...a, approval_status } : a));
    } catch (err) {
      alert('Action failed');
    }
  };

  const handleRequestChanges = async (id, message, type) => {
    try {
      if (type === 'products') {
        await api.patch(`/api/admin/butcher-products/${id}/request-changes`, { message });
      } else {
        await api.patch(`/api/admin/animals/${id}/request-changes`, { message });
        setAnimals(prev => prev.map(a => a.id === id ? { ...a, status: 'pending' } : a));
      }
    } catch (err) {
      alert('Failed to send request');
    }
  };

  return {
    // state
    users, userStats, animals, sales, salesStats, inquiries,
    loadingUsers, loadingData,
    error, platformStats,
    // actions
    fetchUsers, fetchStats, fetchAnimals, fetchSales, fetchInquiries, handleModAction,
    butcherProducts, fetchButcherProducts, handleProductModAction,
    breedStats, recentActivity, fetchBreedStats, fetchRecentActivity, fetchPlatformStats,
    auctions, fetchAuctions, handleAuctionApproval, handleRequestChanges,
  };
}